import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppIconComponent } from '@app/components/app-icon/app-icon.component';
import { MyAppsStore } from '@app/store/myapps.store';
import { SpacesStore } from '@app/store/spaces.store';
import WorkspaceStore from '@app/store/workspace.store';
import { User } from '@interfaces/user';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NavigationService } from '@services/navigation.service';
import { UserService } from '@services/user.service';
import { WebsocketService } from '@services/websocket.service';
import { WorkspaceService } from '@services/workspace.service';
import { getTranslation } from '@shared/translate-helper';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-create-workspace-modal',
  templateUrl: './create-workspace-modal.component.html',
  styleUrls: ['./create-workspace-modal.component.scss'],
  standalone: true,
  imports: [FormsModule, TranslateModule, AppIconComponent],
})
export class CreateWorkspaceModalComponent implements OnInit {
  public workspaceName: string = '';
  public isCreating: boolean = false;

  public readonly workspaceStore: InstanceType<typeof WorkspaceStore> = inject(WorkspaceStore);
  public readonly spacesStore: InstanceType<typeof SpacesStore> = inject(SpacesStore);
  public readonly myAppsStore: InstanceType<typeof MyAppsStore> = inject(MyAppsStore);

  constructor(
    private activeModal: NgbActiveModal,
    private workspaceService: WorkspaceService,
    private userService: UserService,
    private navigationService: NavigationService,
    private websocketService: WebsocketService,
    private translate: TranslateService,
    private spinner: NgxSpinnerService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {}

  closeDialog() {
    this.activeModal.dismiss();
  }

  createNewWorkspace(): void {
    if (!this.workspaceName.trim()) {
      return;
    }
    this.isCreating = true;
    this.spinner.show();

    // Step 1: Create new workspace using the service directly
    this.workspaceService.createNewWorkspace(this.workspaceName).subscribe({
      next: (createResult: any) => {
        const activateWorkspaceData = {
          userUUID: this.userService.getUserData()?.uuid || '',
          companyUUID: createResult.uuid,
        };
        // Step 2: Activate the newly created workspace
        this.workspaceService.activateNewWorkspace(activateWorkspaceData).subscribe({
          next: () => {
            // Step 3: Switch to the new workspace
            const userData = {
              email: this.userService.getUserData()?.email,
              company: {
                uuid: createResult.uuid,
              },
            } as Partial<User>;
            this.workspaceStore
              .switchWorkspace(userData)
              .subscribe({
                next: () => {
                  this.spacesStore.loadSpaces(true);
                  this.myAppsStore.loadActiveConnectors(true);
                  this.navigationService.navigateToSpaces();
                  this.messageService.clear();
                  this.websocketService.closeWebSocket();
                  this.websocketService.initializeWebSocket();
                  getTranslation(this.translate, [
                    'switch_workspace_success',
                    'switch_workspace_description',
                  ]).subscribe((res: any) => {
                    this.messageService.add({
                      severity: 'info',
                      summary: res['switch_workspace_success'],
                      detail: res['switch_workspace_description']?.replace('{{workspaceName}}', createResult.name),
                      life: 3000,
                    });
                  });
                  this.activeModal.close();
                },
              })
              .add(() => {
                this.spinner.hide();
                this.isCreating = false;
              });
          },
        });
      },
      error: () => {
        this.translate.get('create_workspace_error').subscribe((res: string) => {
          this.handleError(res);
        });
      },
    });
  }

  private handleError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('error'),
      detail: message,
      life: 3000,
    });
    this.isCreating = false;
    this.spinner.hide();
  }
}
